import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import {
  LcStockTransfertPhaseRef,
  mergeLcStockTransfertDetailsForModal,
  pickLcTransfertPhaseIdForEnvoye,
  pickLcTransfertPhaseIdForRecue,
  pickLcTransfertPrimaryPhaseId,
} from './logiciel-caisse-stock-transfert-modal-detail.util';
import {
  appendLcUserStationParams,
  lcApiBase,
  lcHttpError,
  logLogicielCaisseHttpError,
  requireLcUserStationIds,
} from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';
import { StockTransfertListFilters } from './logiciel-caisse-stock-transfert.filters';
import { mapStockTransfertApiItemToTableRow } from './logiciel-caisse-stock-transfert-map.util';

export {
  mapStockTransfertApiItemToTableRow,
  stockTransfertIsReceptionLabel,
} from './logiciel-caisse-stock-transfert-map.util';

export { StockTransfertListFilters } from './logiciel-caisse-stock-transfert.filters';

export interface StockTransfertCounts {
  tous?: number;
  en_cours: number;
  cree?: number;
  annule?: number;
  valide?: number;
  en_circulation?: number;
  livraison: number;
  livraison_reception: number;
  /** Menu Bon réception — transferts soldés (is_reception = 1). */
  reception?: number;
  clos: number;
  non_vus?: StockTransfertNonVusCounts;
}

export interface StockTransfertNonVusCounts {
  total: number;
  manuelle: number;
  reasort: number;
}

export interface StockTransfertNotificationApiItem {
  id: number;
  transfert_id: number;
  num_document: string;
  origine: 'manuelle' | 'reasort';
  type: string;
  title: string;
  body: string;
  read: boolean;
  created_at: string;
}

export interface StockTransfertTableRow {
  id: number;
  'N° DOCUMENT': string;
  'DATE TRANSFERT': string;
  'QUANTITÉ': string;
  DÉPART: string;
  RÉCEPTION: string;
  STATUT: string;
  NOTE: string;
  _is_reception: number;
  _circulation_cree?: boolean;
  isGroupParent?: boolean;
  children?: StockTransfertTableChildRow[];
  /** Affichage liste : libellé + code sous le nom (Départ / Réception). */
  _entrepot_depart_nom?: string;
  _entrepot_depart_code?: string;
  _entrepot_reception_nom?: string;
  _entrepot_reception_code?: string;
  /** Champs LC `stock_transfert` + jointure entrepôts (liste API). */
  entrepot_depart_libelle?: string | null;
  entrepot_depart_code?: string | null;
  entrepot_reception_libelle?: string | null;
  entrepot_reception_code?: string | null;
  entrepot_id_depart?: number | null;
  entrepot_id_reception?: number | null;
}

/** Réponse PATCH LC (fork multi-phase : même num_document, nouvel id). */
export interface StockTransfertPatchResult {
  transfert?: { id: number; is_reception?: number; num_document?: string };
  previous_transfert_id?: number;
  forked?: boolean;
}

/** Id LC à utiliser après PATCH (nouvelle fiche si forked). */
export function resolveStockTransfertIdAfterPatch(
  previousId: number,
  res: StockTransfertPatchResult | null | undefined
): number {
  const next = res?.transfert?.id;
  if (res?.forked && next != null && Number(next) > 0) {
    return Number(next);
  }
  return previousId;
}

export interface StockTransfertTableChildRow {
  id: number;
  _is_reception: number;
  _transfertChild?: boolean;
  'N° DOCUMENT': string;
  'DATE TRANSFERT': string;
  'QUANTITÉ': string;
  DÉPART: string;
  RÉCEPTION: string;
  STATUT: string;
  NOTE: string;
  _transfertCodeBarre?: string;
  _transfertCouleur?: string;
  _transfertTaille?: string;
}

export interface StockTransfertDetailBarcode {
  product_id: number | null;
  code_barre: string;
  /** Qté expédiée (lignes LC « transfert envoyé », is_reception ≠ réception terminée). */
  quantite: number;
  quantite_envoyee?: number;
  /** Qté réceptionnée (lignes LC « transfert réceptionné », is_reception = réception terminée). */
  quantite_recue?: number;
  couleur?: string;
  taille?: string;
  /** Description courte saisie sur le document ERP (stock_transfert_ligne_document.note). */
  description?: string | null;
  note?: string | null;
}

export interface StockTransfertDetailLigne {
  libelle_article: string;
  reference: string;
  description?: string | null;
  barcodes: StockTransfertDetailBarcode[];
}

export interface StockTransfertReceptionEtatVariante {
  product_id: number | null;
  code_barre: string;
  couleur: string;
  taille: string;
  quantite_envoyee: number;
  quantite_recue: number;
}

export interface StockTransfertReceptionEtat {
  variantes: StockTransfertReceptionEtatVariante[];
}

/** Cumul par variante depuis `stock_transfert_ligne_document` (is_reception = 1). */
export interface StockTransfertStldQuantiteRecue {
  product_id: number | null;
  code_barre: string;
  couleur: string;
  taille: string;
  quantite_recue: number;
}

export interface StockTransfertDetail {
  id: number;
  num_document: string;
  date_transfert: string;
  entrepot_id_depart: number;
  entrepot_id_reception: number;
  note: string | null;
  is_reception: number;
  /** Fiche circulation : bon réception LC déjà créé. */
  bon_reception_cree?: boolean;
  /** Fiche transfert entrepôt : circulation déjà créée (même num_document). */
  circulation_cree?: boolean;
  lignes: StockTransfertDetailLigne[];
  /** Cumul réceptions partielles / réception LC (GET detail). */
  reception_etat?: StockTransfertReceptionEtat;
  reception_quantites_stld?: StockTransfertStldQuantiteRecue[];
  /** Correction stock disponible PV déjà appliquée (approuver transfert). */
  pv_ecart_stock_corrected?: boolean;
}

export interface ReasortTestAutoResult {
  created: Array<{
    id: number;
    num_document?: string;
    entrepot_id_depart: number;
    entrepot_id_reception: number;
    lignes: number;
  }>;
  summary: {
    message: string;
    transfers: number;
    lines: number;
  };
  warnings: string[];
}

export type StockTransfertCreateBody = {
  date_transfert: string;
  entrepot_id_depart: number;
  entrepot_id_reception: number;
  origine?: 'manuelle' | 'reasort';
  note?: string | null;
  /** BT issu PV d'écart : quantités signées, pas de réservation stock à la création LC. */
  from_pv_ecart?: boolean;
  /** Brouillon à la sortie (Retour) sans ligne produit encore saisie. */
  allow_empty_lignes?: boolean;
  lignes: Array<{
    libelle_article: string;
    reference: string;
    description?: string | null;
    /** Description produit → colonne stock_transfert_ligne_document.note */
    note?: string | null;
    allocations: Array<{
      product_id: number | null;
      code_barre: string | null;
      quantite: number;
      selected: boolean;
      quantite_envoyee?: number;
      quantite_recue?: number;
      couleur?: string | null;
      taille?: string | null;
      description?: string | null;
      note?: string | null;
    }>;
  }>;
};

export type StockTransfertApiTab =
  | 'tous'
  | 'en_cours'
  | 'cree'
  | 'annule'
  | 'valide'
  | 'en_circulation'
  | 'clos'
  | 'livraison'
  | 'livraison_reception'
  | 'reception';

interface StockTransfertListResponse {
  success: boolean;
  items?: StockTransfertTableRow[];
  pagination?: { total: number; page: number; limit: number };
  error?: string;
}

interface StockTransfertByUserStationResponse {
  success: boolean;
  user_id?: number;
  station_id?: number;
  items?: Record<string, unknown>[];
  pagination?: { total: number; page: number; limit: number };
  total?: number;
  error?: string;
}

interface StockTransfertCountsResponse {
  success: boolean;
  counts?: StockTransfertCounts;
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseStockTransfertService {
  private readonly listRefresh$ = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService
  ) {}

  private baseUrl(): string {
    return `${lcApiBase(this.authService)}/api/stock-transferts`;
  }

  onListRefresh(): Observable<void> {
    return this.listRefresh$.asObservable();
  }

  notifyListRefresh(): void {
    this.listRefresh$.next();
  }

  getCounts(options?: { origine?: 'manuelle' | 'reasort' }): Observable<StockTransfertCounts> {
    let params = new HttpParams();
    if (options?.origine) {
      params = params.set('origine', options.origine);
    }
    try {
      requireLcUserStationIds(this.authService);
      params = appendLcUserStationParams(params, this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    return this.http
      .get<StockTransfertCountsResponse>(`${this.baseUrl()}/counts`, { params })
      .pipe(
        map((res) => {
          if (!res?.success || !res.counts) {
            throw new Error(res?.error || 'Réponse compteurs invalide');
          }
          const c = res.counts;
          const en_cours = c.en_cours ?? 0;
          const cree = c.cree ?? 0;
          const annule = c.annule ?? 0;
          const valide = c.valide ?? c.livraison ?? 0;
          const en_circulation = c.en_circulation ?? 0;
          const livraison = c.livraison ?? valide + en_circulation;
          const livraison_reception = c.livraison_reception ?? 0;
          const reception = c.reception ?? livraison_reception;
          const clos = c.clos ?? 0;
          return {
            tous: c.tous ?? cree + annule + valide,
            en_cours,
            cree,
            annule,
            valide,
            en_circulation,
            livraison,
            livraison_reception,
            reception,
            clos,
            non_vus: c.non_vus
              ? {
                  total: c.non_vus.total ?? 0,
                  manuelle: c.non_vus.manuelle ?? 0,
                  reasort: c.non_vus.reasort ?? 0,
                }
              : { total: 0, manuelle: 0, reasort: 0 },
          };
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur compteurs transferts.'))
        )
      );
  }

  list(
    params: {
      tab: StockTransfertApiTab;
      page: number;
      limit: number;
    } & StockTransfertListFilters
  ): Observable<{ items: StockTransfertTableRow[]; pagination: { total: number; page: number; limit: number } }> {
    try {
      requireLcUserStationIds(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    let httpParams = new HttpParams()
      .set('tab', params.tab)
      .set('page', String(params.page))
      .set('limit', String(params.limit));
    if (params.date_debut?.trim()) {
      httpParams = httpParams.set('date_debut', params.date_debut.trim());
    }
    if (params.date_fin?.trim()) {
      httpParams = httpParams.set('date_fin', params.date_fin.trim());
    }
    if (params.mot_cle?.trim()) {
      httpParams = httpParams.set('mot_cle', params.mot_cle.trim());
    }
    if (params.entrepot_depart_ids?.length) {
      httpParams = httpParams.set('entrepot_depart_ids', params.entrepot_depart_ids.join(','));
    }
    if (params.entrepot_reception_ids?.length) {
      httpParams = httpParams.set('entrepot_reception_ids', params.entrepot_reception_ids.join(','));
    }
    if (params.entrepot_ids?.length) {
      httpParams = httpParams.set('entrepot_ids', params.entrepot_ids.join(','));
    }
    if (params.origine === 'manuelle' || params.origine === 'reasort') {
      httpParams = httpParams.set('origine', params.origine);
    }
    if (params.is_reception_in?.length) {
      httpParams = httpParams.set('is_reception_in', params.is_reception_in.join(','));
    }
    if (params.statut_phase?.trim()) {
      httpParams = httpParams.set('statut_phase', params.statut_phase.trim());
    }
    httpParams = appendLcUserStationParams(httpParams, this.authService);
    return this.http.get<StockTransfertListResponse>(this.baseUrl(), { params: httpParams }).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items) || !res.pagination) {
          throw new Error(res?.error || 'Réponse liste invalide');
        }
        const items = res.items.map((row) =>
          mapStockTransfertApiItemToTableRow((row ?? {}) as Record<string, unknown>)
        );
        return { items, pagination: res.pagination };
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur chargement transferts.'))
      )
    );
  }

  /**
   * GET /api/stock-transferts/by-user-station — liste LC par user_id + station_id.
   */
  listByUserStation(
    params: {
      tab?: StockTransfertApiTab | 'tous';
      page: number;
      limit: number;
    } & StockTransfertListFilters
  ): Observable<{ items: StockTransfertTableRow[]; pagination: { total: number; page: number; limit: number } }> {
    const ids = this.authService.readLcIdsFromLocalStorage();
    if (!ids) {
      return throwError(
        () =>
          new Error(
            'Session logiciel caisse incomplète : reconnectez-vous (user_id / station_id manquants).'
          )
      );
    }
    let httpParams = new HttpParams()
      .set('user_id', String(ids.userId))
      .set('station_id', String(ids.stationId))
      .set('tab', params.tab ?? 'tous')
      .set('page', String(params.page))
      .set('limit', String(params.limit));
    if (params.date_debut?.trim()) {
      httpParams = httpParams.set('date_debut', params.date_debut.trim());
    }
    if (params.date_fin?.trim()) {
      httpParams = httpParams.set('date_fin', params.date_fin.trim());
    }
    if (params.mot_cle?.trim()) {
      httpParams = httpParams.set('mot_cle', params.mot_cle.trim());
    }
    if (params.entrepot_depart_ids?.length) {
      httpParams = httpParams.set('entrepot_depart_ids', params.entrepot_depart_ids.join(','));
    }
    if (params.entrepot_reception_ids?.length) {
      httpParams = httpParams.set('entrepot_reception_ids', params.entrepot_reception_ids.join(','));
    }
    if (params.entrepot_ids?.length) {
      httpParams = httpParams.set('entrepot_ids', params.entrepot_ids.join(','));
    }
    if (params.origine === 'manuelle' || params.origine === 'reasort') {
      httpParams = httpParams.set('origine', params.origine);
    }
    if (params.is_reception_in?.length) {
      httpParams = httpParams.set('is_reception_in', params.is_reception_in.join(','));
    }
    if (params.statut_phase?.trim()) {
      httpParams = httpParams.set('statut_phase', params.statut_phase.trim());
    }
    return this.http
      .get<StockTransfertByUserStationResponse>(`${this.baseUrl()}/by-user-station`, {
        params: httpParams,
      })
      .pipe(
        map((res) => {
          if (!res?.success || !Array.isArray(res.items)) {
            throw new Error(res?.error || 'Réponse by-user-station invalide');
          }
          const page = res.pagination?.page ?? params.page;
          const limit = res.pagination?.limit ?? params.limit;
          const total =
            res.pagination?.total ??
            (typeof res.total === 'number' ? res.total : res.items.length);
          const items = res.items.map((row) =>
            mapStockTransfertApiItemToTableRow(row ?? {})
          );
          return {
            items,
            pagination: { total, page, limit },
          };
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur chargement transferts (by-user-station).'))
        )
      );
  }

  /**
   * POST réception — direct back LC si session caisse ; sinon proxy ERP (mdp acces_logiciel_caisse).
   */
  receptionner(
    id: number,
    options?: { stockOnlyPartial?: boolean }
  ): Observable<StockTransfertPatchResult> {
    const hasLcToken = !!this.authService.getLogicielCaisseToken()?.trim();
    const erpCtx = this.authService.getErpContext();
    if (!hasLcToken && erpCtx) {
      return this.receptionnerViaErpProxy(id, options);
    }
    return this.receptionnerDirectLc(id, options).pipe(
      catchError((err: HttpErrorResponse) => {
        const msg = lcHttpError(err, '').message;
        const canProxy =
          erpCtx &&
          (err.status === 404 ||
            err.status === 0 ||
            /cannot get/i.test(msg) ||
            /not found/i.test(msg));
        if (canProxy) {
          return this.receptionnerViaErpProxy(id, options);
        }
        return throwError(() => err);
      })
    );
  }

  private receptionnerViaErpProxy(
    id: number,
    options?: { stockOnlyPartial?: boolean }
  ): Observable<StockTransfertPatchResult> {
    const erpCtx = this.authService.getErpContext();
    if (!erpCtx) {
      return throwError(
        () => new Error('Session ERP requise pour confirmer la réception sans logiciel caisse.')
      );
    }
    let params = new HttpParams();
    if (Number.isFinite(erpCtx.erpStationId) && erpCtx.erpStationId > 0) {
      params = params.set('station_id', String(Math.floor(erpCtx.erpStationId)));
    }
    if (erpCtx.code_station?.trim()) {
      params = params.set('code_station', erpCtx.code_station.trim());
    }
    const erpBase = String(environment.apiUrl || '').replace(/\/$/, '');
    const body: Record<string, unknown> = {};
    if (options?.stockOnlyPartial === true) {
      body.stock_only_partial = true;
    }
    return this.http
      .post<{ success: boolean; error?: string } & StockTransfertPatchResult>(
        `${erpBase}/api/logiciel-caisse/stock-transferts/${id}/reception`,
        body,
        { params }
      )
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Réception refusée (proxy ERP).');
          }
          return {
            transfert: res.transfert,
            previous_transfert_id: res.previous_transfert_id,
            forked: res.forked,
          };
        }),
        catchError((err: HttpErrorResponse) => {
          const apiErr = lcHttpError(err, 'Erreur réception transfert (ERP).');
          logLogicielCaisseHttpError('POST ERP logiciel-caisse/stock-transferts/reception', err, {
            transfertId: id,
            message: apiErr.message,
          });
          return throwError(() => apiErr);
        })
      );
  }

  private receptionnerDirectLc(
    id: number,
    options?: { stockOnlyPartial?: boolean }
  ): Observable<StockTransfertPatchResult> {
    const ids = this.authService.readLcIdsFromLocalStorage();
    const payload: Record<string, unknown> =
      ids != null ? { user_id: ids.userId, station_id: ids.stationId } : {};
    if (options?.stockOnlyPartial === true) {
      payload.stock_only_partial = true;
    }
    return this.http
      .post<{ success: boolean; error?: string } & StockTransfertPatchResult>(
        `${this.baseUrl()}/${id}/reception`,
        payload
      )
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Réception refusée');
          }
          return {
            transfert: res.transfert,
            previous_transfert_id: res.previous_transfert_id,
            forked: res.forked,
          };
        }),
        catchError((err: HttpErrorResponse) => {
          const apiErr = lcHttpError(err, 'Erreur réception transfert.');
          logLogicielCaisseHttpError('POST stock-transfert/reception', err, {
            transfertId: id,
            payload,
            message: apiErr.message,
          });
          return throwError(() => apiErr);
        })
      );
  }

  /** Réservation stock départ + passage en Créé (is_reception = 2). */
  approuverDepartStock(id: number): Observable<{ transfert?: unknown }> {
    const ids = this.authService.readLcIdsFromLocalStorage();
    const payload = ids != null ? { user_id: ids.userId, station_id: ids.stationId } : {};
    return this.http
      .post<{ success: boolean; error?: string; transfert?: unknown }>(
        `${this.baseUrl()}/${id}/approuver-depart`,
        payload
      )
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Approbation transfert refusée');
          }
          return { transfert: res.transfert };
        }),
        catchError((err: HttpErrorResponse) => {
          logLogicielCaisseHttpError('POST stock-transfert/approuver-depart', err, { transfertId: id });
          return throwError(() => lcHttpError(err, 'Erreur approbation transfert (stock départ).'));
        })
      );
  }

  /** PUT — remplace entête + lignes (statut Bon transfert ou Créé). */
  replace(id: number, body: StockTransfertCreateBody): Observable<{ transfert?: unknown }> {
    const ids = this.authService.readLcIdsFromLocalStorage();
    const payload =
      ids != null
        ? { ...body, user_id: ids.userId, station_id: ids.stationId }
        : body;
    return this.http
      .put<{ success: boolean; error?: string; transfert?: unknown }>(
        `${this.baseUrl()}/${id}`,
        payload
      )
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Mise à jour transfert refusée');
          }
          return { transfert: res.transfert };
        }),
        catchError((err: HttpErrorResponse) => {
          logLogicielCaisseHttpError('PUT stock-transfert', err, { transfertId: id, payload });
          return throwError(() => lcHttpError(err, 'Erreur mise à jour du transfert.'));
        })
      );
  }

  patch(
    id: number,
    body: { note?: string | null; is_reception?: number; notification_vue?: number }
  ): Observable<StockTransfertPatchResult> {
    const ids = this.authService.readLcIdsFromLocalStorage();
    const payload =
      ids != null
        ? { ...body, user_id: ids.userId, station_id: ids.stationId }
        : body;
    return this.http
      .patch<{ success: boolean; error?: string } & StockTransfertPatchResult>(
        `${this.baseUrl()}/${id}`,
        payload
      )
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Mise à jour refusée');
          }
          return {
            transfert: res.transfert,
            previous_transfert_id: res.previous_transfert_id,
            forked: res.forked,
          };
        }),
        catchError((err: HttpErrorResponse) => {
          logLogicielCaisseHttpError('PATCH stock-transfert', err, {
            transfertId: id,
            payload,
          });
          return throwError(() => lcHttpError(err, 'Erreur mise à jour transfert.'));
        })
      );
  }

  private lcListContextParams(): HttpParams | null {
    const ids = this.authService.readLcIdsFromLocalStorage();
    if (!ids) {
      return null;
    }
    return new HttpParams()
      .set('user_id', String(ids.userId))
      .set('station_id', String(ids.stationId));
  }

  /**
   * GET /api/stock-transferts/by-num-document?num_document=&user_id=&station_id=
   * Sans is_reception : 200 (une fiche) ou 400 avec `phases[]` si plusieurs fiches.
   */
  lookupPhasesByNumDocument(numDocument: string): Observable<LcStockTransfertPhaseRef[]> {
    const paramsBase = this.lcListContextParams();
    const num = String(numDocument ?? '').trim();
    if (!paramsBase || !num) {
      return throwError(
        () => new Error('N° document ou session logiciel caisse manquant.')
      );
    }
    const params = paramsBase.set('num_document', num);
    return this.http
      .get<{
        success: boolean;
        detail?: StockTransfertDetail;
        phases?: LcStockTransfertPhaseRef[];
        error?: string;
      }>(`${this.baseUrl()}/by-num-document`, { params })
      .pipe(
        map((res) => {
          if (res?.success && res.detail) {
            return [
              {
                id: Number(res.detail.id),
                is_reception: Number(res.detail.is_reception),
              },
            ];
          }
          if (Array.isArray(res?.phases) && res.phases.length > 0) {
            return res.phases.map((p) => ({
              id: Number(p.id),
              is_reception: Number(p.is_reception),
            }));
          }
          throw new Error(res?.error || 'Transfert introuvable');
        }),
        catchError((err: HttpErrorResponse) => {
          const body = err.error as {
            phases?: LcStockTransfertPhaseRef[];
            error?: string;
          } | null;
          if (err.status === 400 && Array.isArray(body?.phases) && body.phases.length > 0) {
            return of(
              body.phases.map((p) => ({
                id: Number(p.id),
                is_reception: Number(p.is_reception),
              }))
            );
          }
          return throwError(() => lcHttpError(err, 'Erreur recherche transfert.'));
        })
      );
  }

  /**
   * Popup œil transfert entrepôt : phases par num_document, puis GET /:id pour
   * QTÉ ENVOYÉE (is_reception 4/2/0) et QTÉ REÇUE (is_reception 1).
   */
  getDetailForTransfertEntrepotModal(
    numDocument: string,
    rowHint?: { id?: number; is_reception?: number }
  ): Observable<StockTransfertDetail> {
    const num = String(numDocument ?? '').trim();
    if (!num) {
      return throwError(() => new Error('N° document manquant'));
    }
    return this.lookupPhasesByNumDocument(num).pipe(
      switchMap((phases) => {
        const primaryId = pickLcTransfertPrimaryPhaseId(phases, rowHint?.id);
        const envoyeId = pickLcTransfertPhaseIdForEnvoye(phases);
        const recuId = pickLcTransfertPhaseIdForRecue(phases);
        if (primaryId == null) {
          return throwError(() => new Error('Aucune fiche transfert pour ce num_document.'));
        }
        const idsToLoad = new Set<number>([primaryId]);
        if (envoyeId != null) {
          idsToLoad.add(envoyeId);
        }
        if (recuId != null) {
          idsToLoad.add(recuId);
        }
        const loads: Record<string, Observable<StockTransfertDetail>> = {};
        for (const id of idsToLoad) {
          loads[`id_${id}`] = this.getById(id);
        }
        return forkJoin(loads).pipe(
          map((bag) => {
            const primary = bag[`id_${primaryId}`];
            const envoye =
              envoyeId != null ? bag[`id_${envoyeId}`] ?? null : null;
            const recu = recuId != null ? bag[`id_${recuId}`] ?? null : null;
            return mergeLcStockTransfertDetailsForModal(primary, envoye, recu);
          })
        );
      })
    );
  }

  /**
   * GET /api/stock-transferts/by-num-document?num_document=&is_reception=&user_id=&station_id=
   */
  getDetailByNumDocument(
    numDocument: string,
    isReception: number
  ): Observable<StockTransfertDetail> {
    const paramsBase = this.lcListContextParams();
    const num = String(numDocument ?? '').trim();
    if (!paramsBase || !num) {
      return throwError(
        () =>
          new Error(
            'Session logiciel caisse incomplète : reconnectez-vous (user_id / station_id manquants).'
          )
      );
    }
    const phase = Number(isReception);
    if (!Number.isFinite(phase)) {
      return throwError(() => new Error('Phase transfert (is_reception) invalide'));
    }
    const params = paramsBase
      .set('num_document', num)
      .set('is_reception', String(Math.trunc(phase)));
    return this.http
      .get<{ success: boolean; detail?: StockTransfertDetail; error?: string }>(
        `${this.baseUrl()}/by-num-document`,
        { params }
      )
      .pipe(
        map((res) => {
          if (!res?.success || !res.detail) {
            throw new Error(res?.error || 'Transfert introuvable');
          }
          return res.detail;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur chargement transfert.'))
        )
      );
  }

  /**
   * GET /api/stock-transferts/:idOrNumDocument?user_id=&station_id=
   * :key = id LC ou num_document (ex. 00000022).
   */
  getDetailByRouteKey(idOrNumDocument: number | string): Observable<StockTransfertDetail> {
    const ids = this.authService.readLcIdsFromLocalStorage();
    if (!ids) {
      return throwError(
        () =>
          new Error(
            'Session logiciel caisse incomplète : reconnectez-vous (user_id / station_id manquants).'
          )
      );
    }
    const key = encodeURIComponent(String(idOrNumDocument).trim());
    const params = new HttpParams()
      .set('user_id', String(ids.userId))
      .set('station_id', String(ids.stationId));
    return this.http
      .get<{ success: boolean; detail?: StockTransfertDetail; error?: string }>(
        `${this.baseUrl()}/${key}`,
        { params }
      )
      .pipe(
        map((res) => {
          if (!res?.success || !res.detail) {
            throw new Error(res?.error || 'Transfert introuvable');
          }
          return res.detail;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur chargement transfert.'))
        )
      );
  }

  getById(id: number): Observable<StockTransfertDetail> {
    return this.getDetailByRouteKey(id);
  }

  reasortTestAuto(): Observable<ReasortTestAutoResult> {
    return this.http
      .post<{
        success: boolean;
        created?: ReasortTestAutoResult['created'];
        summary?: ReasortTestAutoResult['summary'];
        warnings?: string[];
        error?: string;
      }>(`${this.baseUrl()}/reasort-test-auto`, {})
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Test réassort refusé');
          }
          return {
            created: res.created ?? [],
            summary: res.summary ?? { message: '', transfers: 0, lines: 0 },
            warnings: res.warnings ?? [],
          };
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur test réassort.'))
        )
      );
  }

  /** Prochain num_document LC (8 chiffres), pour l’en-tête « nouveau transfert ». */
  previewNextNumDocument(): Observable<string> {
    return this.http
      .get<{ success: boolean; next_num?: string; error?: string }>(
        `${this.baseUrl()}/next-num-document`
      )
      .pipe(
        map((res) => {
          if (!res?.success || !res.next_num?.trim()) {
            throw new Error(res?.error || 'Réponse numéro transfert invalide');
          }
          return res.next_num.trim();
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur chargement prochain numéro transfert.'))
        )
      );
  }

  create(body: StockTransfertCreateBody): Observable<{ id: number; num_document: string }> {
    return this.http
      .post<{
        success: boolean;
        id?: number;
        num_document?: string;
        error?: string;
      }>(this.baseUrl(), body)
      .pipe(
        map((res) => {
          if (!res?.success || res.id == null || res.num_document == null) {
            throw new Error(res?.error || 'Création du transfert refusée');
          }
          return { id: res.id, num_document: res.num_document };
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur création transfert.'))
        )
      );
  }

  listNotifications(limit = 50): Observable<StockTransfertNotificationApiItem[]> {
    const params = new HttpParams().set('limit', String(limit));
    return this.http
      .get<{
        success: boolean;
        items?: StockTransfertNotificationApiItem[];
        error?: string;
      }>(`${this.baseUrl()}/notifications`, { params })
      .pipe(
        map((res) => {
          if (!res?.success || !Array.isArray(res.items)) {
            throw new Error(res?.error || 'Réponse notifications invalide');
          }
          return res.items;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur chargement notifications.'))
        )
      );
  }

  markAllNotificationsRead(): Observable<number> {
    return this.http
      .post<{ success: boolean; marked?: number; error?: string }>(
        `${this.baseUrl()}/notifications/read-all`,
        {}
      )
      .pipe(
        map((res) => {
          if (!res?.success) {
            throw new Error(res?.error || 'Marquage notifications refusé');
          }
          return res.marked ?? 0;
        }),
        catchError((err: HttpErrorResponse) =>
          throwError(() => lcHttpError(err, 'Erreur marquage notifications.'))
        )
      );
  }
}
